// useSignup (contract 06 §9) — wraps AuthSignupFlow. Live validation surfaces `validation.*`
// field keys; a taken email is the `emailExists` inline state; server field errors bind to the
// form. All copy is i18n keys. Submit is disabled by the screen until valid + terms.
import { reactive, ref, type Ref } from 'vue'
import { useRouter } from 'vue-router'

import { AuthSignupFlow } from '../flows/AuthSignupFlow'
import { AuthError } from '../models/auth.error'
import type { SignupPayload } from '../models/auth.types'
import type { SignupField } from '../validators/AuthValidator'

type FieldErrors = Partial<Record<SignupField, string>>

export interface UseSignup {
  user: SignupPayload
  emailExists: Ref<boolean>
  pending: Ref<boolean>
  error: Ref<string | null>
  fieldErrors: Ref<FieldErrors>
  submit: (terms: boolean) => Promise<void>
  signupWithGoogle: () => void
}

export function useSignup(): UseSignup {
  const router = useRouter()
  const flow = new AuthSignupFlow()

  const user = reactive<SignupPayload>({ email: '', password: '', name: '', company_name: '' })
  const emailExists = ref(false)
  const pending = ref(false)
  const error = ref<string | null>(null)
  const fieldErrors = ref<FieldErrors>({})

  async function submit(terms: boolean): Promise<void> {
    pending.value = true
    error.value = null
    emailExists.value = false
    fieldErrors.value = {}
    try {
      const outcome = await flow.submit({ ...user }, terms)
      if (!outcome.ok) {
        fieldErrors.value = outcome.fieldErrors
        return
      }
      await router.push({ name: 'SignUpMfa', query: { email: outcome.email } })
    } catch (e) {
      const authError = AuthError.from(e)
      if (authError.code === 'emailExists') {
        emailExists.value = true
      } else {
        error.value = authError.i18nKey
        for (const [field, messages] of Object.entries(authError.fields)) {
          const first = messages[0]
          if (first) fieldErrors.value[field as SignupField] = first
        }
      }
    } finally {
      pending.value = false
    }
  }

  return {
    user,
    emailExists,
    pending,
    error,
    fieldErrors,
    submit,
    signupWithGoogle: () => flow.startGoogleSignupRedirect(),
  }
}
